import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import './index.scss'

let ctx: CanvasRenderingContext2D
const ratio = (window.devicePixelRatio || 1)

// 分别取4个对角点和居中4个点
const getHightlightPoints = (x: number, y: number, w: number, h: number) => {
  return [
    [x - 2, y - 2, 4, 4],
    [x + w / 2 - 2, y - 2, 4, 4],
    [x + w - 2, y - 2, 4, 4],
    [x - 2, y + h / 2 - 2, 4, 4],
    [x + w - 2, y + h / 2 - 2, 4, 4],
    [x - 2, y + h - 2, 4, 4],
    [x + w / 2 - 2, y + h - 2, 4, 4],
    [x + w - 2, y + h - 2, 4, 4],
  ]
}

// 取3等分线，形成九宫格
const getDashLines = (x: number, y: number, w: number, h: number) => {
  return [
    [x, y + h / 3, x + w, y + h / 3],
    [x, y + 2 * h / 3, x + w, y + 2 * h / 3],
    [x + w / 3, y, x + w / 3, y + h],
    [x + 2 * w / 3, y, x + 2 * w / 3, y + h]
  ]
}

export interface IImageEditorProps {
  width?: number
  height?: number
  file?: File,
  url?: string,
  className?: string,
  regions?: number[],
  onRegionsChange?: any
}

function SimpleImageEditor({ 
  file,
  url = undefined,
  className,
  regions,
  onRegionsChange, // todo
  width: canvasExpectWidth = 500,
  height: canvasExpectHeight = 300,
}: IImageEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const canvasSize = useRef([canvasExpectWidth, canvasExpectHeight])
  const imageScale = useRef(1)

  const resetCanvas = () => {
    if (!imgRef.current) return
    if (!canvasRef.current) return
    const image = imgRef.current
    const { width, height } = image
    const imgRatio = width / height
    let scale = 1
    if (width < canvasExpectWidth && height < canvasExpectHeight) {
      scale = 1
    }
    if (imgRatio > 1) {
      scale = canvasExpectWidth / width
    } else {
      scale = canvasExpectHeight / height
    }
    imageScale.current = scale

    let canvasWidth = Math.min(canvasExpectWidth, width * scale)
    let canvasHeight = Math.min(canvasExpectHeight, height * scale)
    canvasSize.current = [canvasWidth, canvasHeight]
    canvasRef.current.style.width = `${canvasWidth}px`
    canvasRef.current.style.height = `${canvasHeight}px`
    canvasRef.current.width = canvasWidth * ratio
    canvasRef.current.height = canvasHeight * ratio
    ctx.scale(ratio, ratio)
  }

  const drawImage = () => {
    if (!imgRef.current) return
    if (!canvasRef.current) return
    const image = imgRef.current
    const { width, height } = image
    
    ctx.save()
    ctx.globalCompositeOperation = 'destination-over'
    const scaleImgWidth = imageScale.current * width
    const scaleImgHeight = imageScale.current * height
    ctx.drawImage(
      image,
      (canvasSize.current[0] - scaleImgWidth) / 2, (canvasSize.current[1] - scaleImgHeight) / 2,
      scaleImgWidth, scaleImgHeight
    )
    ctx.restore()
  }

  const drawRoundRect = (x: number, y: number, w: number, h: number, r: number) => {
    if (w < 2 * r) r = w / 2
    if (h < 2 * r) r = h / 2
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.arcTo(x + w, y, x + w, y + h, r)
    ctx.arcTo(x + w, y + h, x, y + h, r)
    ctx.arcTo(x, y + h, x, y, r)
    ctx.arcTo(x, y, x + w, y, r)
    ctx.closePath()
    ctx.fill()
  }

  const drawDashLine = (fromX: number, fromY: number, toX: number, toY: number) => {
    ctx.beginPath()
    ctx.setLineDash([2, 4])
    ctx.moveTo(fromX, fromY)
    ctx.lineTo(toX, toY)
    ctx.closePath()
    ctx.stroke()
  }

  const drawRegion = () => {
    if (!Array.isArray(regions)) return
    if (!canvasRef.current) return
    ctx.clearRect(0, 0, canvasSize.current[0], canvasSize.current[1])
    // draw mask
    ctx.save()
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
    ctx.fillRect(0, 0, canvasSize.current[0], canvasSize.current[1])
    ctx.globalCompositeOperation = 'source-atop'
    ctx.restore()

    ctx.save()
    // x1, x2, y1, y2
    const startX = regions[0] * imageScale.current
    const startY = regions[2] * imageScale.current
    const rectWidth = (regions[1] - regions[0]) * imageScale.current
    const rectHeight = (regions[3] - regions[2]) * imageScale.current
    console.log(regions)
    ctx.clearRect(startX, startY, rectWidth, rectHeight)
    ctx.strokeStyle = '#1377cc'
    ctx.strokeRect(startX, startY, rectWidth, rectHeight)
    ctx.fillStyle = '#1377cc'
    const points = getHightlightPoints(startX, startY, rectWidth, rectHeight)
    // draw highlight points
    points.forEach(p => drawRoundRect(p[0], p[1], p[2], p[3], Math.PI))
    // draw dash line 
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(255, 255, 255, .5)'
    const dashes = getDashLines(startX, startY, rectWidth, rectHeight)
    dashes.forEach(line => drawDashLine(line[0], line[1], line[2], line[3]))
    ctx.restore()
  }

  const onDraw = () => {
    resetCanvas()
    drawRegion()
    drawImage()
  }

  useEffect(() => {
    try {
      ctx = canvasRef.current!.getContext('2d') as CanvasRenderingContext2D

      const _URL = window.URL || window.webkitURL
      const image = new Image()
      const source = url || _URL.createObjectURL(file as any)
      image.onload = () => {
        imgRef.current = image
        onDraw()
      }
      image.src = source
      setTimeout(() => {
        !url && _URL.revokeObjectURL(source)
      }, 10)
    } catch(e) {
      console.error(e)
      alert(e)
    }
  }, [file, url])

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    // todo handle regions change
  }

  const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    // todo handle regions change
  }

  return (
    <div className={classNames('simple-image-editor', className)}>
      <canvas
        ref={canvasRef}
        onMouseMove={onMouseMove}
        onMouseDown={onMouseDown}
      >browser does not support canvas
      </canvas>
    </div>
  )
}

export default SimpleImageEditor